import { Helmet } from "react-helmet";
import {
  Button,
  Dropdown,
  EmptyState,
  Flex,
  SimpleTable,
  Tag,
  Typography,
} from "djuno-design";
import useServiceType from "../../hooks/useServiceType";
import { DeleteModal } from "../../components/modals/QuestionModal";
import { useTour } from "../../providers/TourProvider";

import RefreshIcon from "./../../assets/icons/arrow-path.svg?react";
import PlusIcon from "./../../assets/icons/plus.svg?react";
import DetailsIcon from "./../../assets/icons/bars-3-bottom-left.svg?react";
import EditIcon from "./../../assets/icons/pencil-square.svg?react";
import ArchiveIcon from "./../../assets/icons/archive-box.svg?react";
import MoreIcon from "./../../assets/icons/more.svg?react";
import ArrowRightIcon from "./../../assets/icons/arrow-right.svg?react";

import { useNavigate } from "react-router-dom";
import {
  DataServicesCreateUrl,
  DataServiceGeneralInformationUrl,
} from "../../utils/urls";
import HighlighterText from "../../components/general/HighlighterText";
import { useSearch } from "../../providers/SearchProvider";
import { DataStreamService } from "../../types/data-straming";
import DBSNodesLights from "../../components/databases/general-information/DBSNodesLights";
import { formatTimestamp } from "../../utils/date";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { useEffect, useMemo, useState } from "react";
import { getDBserviceIcon } from "../../components/databases/services/DatabaseServicesTab";
import {
  DatabaseRefreshStatus,
  deleteDBSAsync,
  getDatabaseServicesAsync,
  handleShowDatabaseRenameModal,
  selectDatabaseServices,
  selectDatabaseServicesActionLoading,
  selectDatabaseServicesLoading,
} from "../../store/database/servicesSlice";
import DatabaseRenameModal from "../../components/databases/services/DatabaseRenameModal";

const DataAnalsisServicesPage = () => {
  const category = "analysis";

  const { serviceType } = useServiceType(
    import.meta.env.VITE_DATA_ANALYSIS_SERVICE_TYPE
  );

  const { run } = useTour("databases");
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { value: searchValue } = useSearch();

  const [deleteService, setDeleteService] = useState<DataStreamService | null>(
    null
  );

  const services = useAppSelector(selectDatabaseServices);
  const loading = useAppSelector(selectDatabaseServicesLoading);

  const dataAnalysisServices = useMemo(() => {
    return services?.filter((s) => s.category === category);
  }, [services]);

  const [filteredServices, setFilteredServices] = useState<DataStreamService[]>(
    []
  );

  const actionLoading = useAppSelector(selectDatabaseServicesActionLoading);

  useEffect(() => {
    dispatch(getDatabaseServicesAsync({}));
  }, [dispatch]);

  useEffect(() => {
    const lookedUpFiles = dataAnalysisServices?.filter((service) =>
      service.description.toLowerCase().includes(searchValue.toLowerCase())
    );
    setFilteredServices(lookedUpFiles);
  }, [dispatch, searchValue, dataAnalysisServices]);

  //refresh the list
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    const intervalTime =
      Number(import.meta.env.VITE_NODES_REFRESH_INTERVAL) || 20000;

    const shouldSetInterval = dataAnalysisServices.some((service) =>
      DatabaseRefreshStatus.includes(service.status)
    );

    if (shouldSetInterval) {
      interval = setInterval(() => {
        dispatch(getDatabaseServicesAsync({ withoutLoading: true }));
      }, intervalTime);
    } else {
      if (interval) clearInterval(interval);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [dispatch, dataAnalysisServices]);

  return (
    <>
      <Helmet>
        <title>
          {import.meta.env.VITE_APP_NAME} | {serviceType?.ServiceName || ""}
        </title>
        <meta name="description" content="" />
      </Helmet>
      <div className="flex items-center justify-between h-20">
        <div className="px-6 items-center justify-between flex flex-1 transition duration-150">
          <Typography.Title level={5} className="!mb-0">
            {serviceType?.ServiceName}
          </Typography.Title>
        </div>
        {/* <div className="mr-6 w-96"></div> */}
      </div>
      <div className="mx-6">
        <div className="py-6">
          <div className="">
            <div className="flex items-center justify-between databases-header">
              <div className="items-center justify-between flex flex-1 transition duration-150">
                <Typography.Text className="font-medium text-lg"></Typography.Text>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => {
                    run();
                  }}
                >
                  Start Tour
                </Button>
                <Button
                  onClick={() => dispatch(getDatabaseServicesAsync({}))}
                  className="refresh-button group"
                  tooltip={{ content: "Refresh" }}
                >
                  <RefreshIcon className="w-4 h-4 group-hover:rotate-90 group-hover:scale-110 transition-all duration-500" />
                </Button>
                <Button
                  uiType="primary"
                  onClick={() => navigate(DataServicesCreateUrl(category))}
                  className="create-database-button group"
                >
                  Create Service
                  <PlusIcon className="w-3 h-3 group-hover:rotate-90 group-hover:scale-110 transition-all duration-500" />
                </Button>
              </div>
            </div>

            <div className="mt-5 w-full database-table">
              <SimpleTable loading={loading} containerClassName="min-h-[240px]">
                <SimpleTable.Head>
                  <SimpleTable.Row>
                    <SimpleTable.TH lable="Name" />
                    <SimpleTable.TH lable="Engine" />
                    <SimpleTable.TH lable="Solution" />
                    <SimpleTable.TH lable="Usable storage" />
                    <SimpleTable.TH lable="Location" />
                    <SimpleTable.TH lable="Nodes" />
                    <SimpleTable.TH lable="Creation date" />
                    <SimpleTable.TH lable="Status" />
                    <SimpleTable.TH lable="" />
                  </SimpleTable.Row>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {filteredServices.map((service, i) => (
                    <SimpleTable.Row
                      key={i}
                      onClick={() =>
                        navigate(
                          DataServiceGeneralInformationUrl(
                            service.engine,
                            service.id
                          )
                        )
                      }
                    >
                      <SimpleTable.TD className="cursor-pointer">
                        <Flex>
                          <HighlighterText
                            searchValue={searchValue}
                            textToHighlight={service.description}
                            className="max-w-[110px] md:max-w-[400px] lg:max-w-[350px] xl:max-w-[400px] truncate"
                          />
                        </Flex>
                      </SimpleTable.TD>
                      <SimpleTable.TD className="cursor-pointer">
                        <Typography.Text
                          className="!text-xs md:!text-sm flex items-center whitespace-nowrap gap-0.5"
                          // type="neutral-alert"
                        >
                          {getDBserviceIcon(service.engine)}
                          {`${service.engine} ${service.version}`}
                        </Typography.Text>
                      </SimpleTable.TD>

                      <SimpleTable.TD className="cursor-pointer">
                        <Typography.Text className="!text-xs md:!text-sm">
                          {service.plan}
                        </Typography.Text>
                      </SimpleTable.TD>

                      <SimpleTable.TD className="cursor-pointer">
                        {service.storage.size.value > 0 && (
                          <Typography.Text className="!text-xs md:!text-sm">
                            {`${service.storage.size.value} ${service.storage.size.unit}`}
                          </Typography.Text>
                        )}
                      </SimpleTable.TD>

                      <SimpleTable.TD className="cursor-pointer">
                        <Typography.Text className="!text-xs md:!text-sm"></Typography.Text>
                      </SimpleTable.TD>

                      <SimpleTable.TD className="cursor-pointer">
                        <DBSNodesLights nodes={service.nodes} />
                      </SimpleTable.TD>

                      <SimpleTable.TD className="cursor-pointer">
                        <Typography.Text className="!text-xs md:!text-sm">
                          {
                            formatTimestamp(service.createdAt, "DD/MM/YYYY")
                              .date
                          }
                        </Typography.Text>
                      </SimpleTable.TD>
                      <SimpleTable.TD className="cursor-pointer">
                        <Tag
                          className="text-xs"
                          color={
                            service.status === "CREATING"
                              ? "warning"
                              : service.status === "READY"
                              ? "success"
                              : "processing"
                          }
                        >
                          {service.status}
                        </Tag>
                      </SimpleTable.TD>

                      <SimpleTable.TD className="w-32 gap-1">
                        <div className="h-full w-full inline-flex items-center justify-center gap-1">
                          <div className="w-8 flex justify-center items-center">
                            <Dropdown
                              anchor="bottom end"
                              itemsClassName="!w-48"
                              menu={[
                                {
                                  key: "1",
                                  label: (
                                    <div className="flex items-center gap-1">
                                      <DetailsIcon className="w-4" />
                                      General Information
                                    </div>
                                  ),
                                  onClick: (_, close) => {
                                    close();
                                    navigate(
                                      DataServiceGeneralInformationUrl(
                                        service.engine,
                                        service.id
                                      )
                                    );
                                  },
                                  disabled: loading,
                                },
                                {
                                  key: "rename",
                                  label: (
                                    <div className="flex items-center gap-1">
                                      <EditIcon className="w-4" />
                                      Rename
                                    </div>
                                  ),
                                  onClick: (_, close) => {
                                    close();
                                    dispatch(
                                      handleShowDatabaseRenameModal(service)
                                    );
                                  },
                                  disabled:
                                    loading || service.status !== "READY",
                                },
                                {
                                  type: "divider",
                                },
                                {
                                  key: "end",
                                  label: (
                                    <div className="flex items-center gap-1">
                                      <ArchiveIcon className="w-4" />
                                      Delete
                                    </div>
                                  ),
                                  danger: true,
                                  onClick: (_, close) => {
                                    close();
                                    setDeleteService(service);
                                  },
                                  disabled:
                                    loading || service.status !== "READY",
                                },
                              ]}
                            >
                              <Button
                                uiType="icon"
                                uiSize="small"
                                className="!px-2"
                              >
                                <MoreIcon className="w-4 h-4" />
                              </Button>
                            </Dropdown>
                          </div>
                          <ArrowRightIcon className="w-4 h-4 dark:text-slate-100 text-slate-800 cursor-pointer" />
                        </div>
                      </SimpleTable.TD>
                    </SimpleTable.Row>
                  ))}

                  {dataAnalysisServices.length === 0 && (
                    <SimpleTable.Row
                      withoutHoverStyle={true}
                      className="h-[200px]"
                    >
                      <SimpleTable.TD colSpan={10} className="!border-0">
                        <EmptyState text="You have not created any data analysis yet" />
                      </SimpleTable.TD>
                    </SimpleTable.Row>
                  )}
                </SimpleTable.Body>
              </SimpleTable>
            </div>
            <DatabaseRenameModal />
            <DeleteModal
              title="Delete a Data Analysis Service"
              isOpen={deleteService !== null}
              onClose={() => setDeleteService(null)}
              description=""
              confirmButtonText="Confirm"
              confirmButtonType="danger"
              confirmButtonClassName="w-full"
              loading={actionLoading}
              onConfirm={async () => {
                if (!deleteService) return;

                const action = await dispatch(
                  deleteDBSAsync({
                    clusterId: deleteService.id,
                    engine: deleteService.engine,
                  })
                );

                if (action.type === "db-services/delete/fulfilled") {
                  dispatch(getDatabaseServicesAsync({}));
                }

                setDeleteService(null);
              }}
              confirmString={deleteService?.description}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default DataAnalsisServicesPage;
